import Info from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { SxProps } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber.ts';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Sections } from '../../../types.ts';
import { useSegmentContext } from '../../utils/context/SegmentContext.tsx';

import { ReachTooltipContent } from './ReachTooltipContent.tsx';
import { createSectionsForReachTooltip } from './utils.ts';

const StyledInfoIcon = (props: SvgIconProps) => (
  <Info
    fontSize="small"
    data-test="reach-info-icon"
    {...props}
    sx={{
      color: theme => theme.palette.text.secondary,
      cursor: 'pointer',
      position: 'relative',
    }}
  />
);

type ReachTooltipProps = {
  id: string;
  reach: number;
  sx?: SxProps;
  removeMargin?: boolean;
};

function TooltipContent({ id, reach }: ReachTooltipProps): JSX.Element {
  const { t } = useTranslation();
  const { useSegmentReachFetch } = useSegmentContext();

  const { segmentReach, loading } = useSegmentReachFetch(id);

  const sections: Sections = useMemo(() => createSectionsForReachTooltip(segmentReach), [segmentReach]);
  const text = t('The total segment reach of {reach} users is associated with the following source ID counts:', {
    reach: abbreviatedNumber(reach),
  });

  return <ReachTooltipContent text={text} sections={sections} loading={loading} />;
}

export function ReachTooltip({ id, reach, sx, removeMargin }: ReachTooltipProps): JSX.Element {
  return (
    <Tooltip placement="right" title={<TooltipContent id={id} reach={reach} />}>
      <Box
        data-test="reach-tooltip-container"
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            ml: removeMargin ? 0 : 1.5,
            borderRadius: '50%',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <IconButton>
          <StyledInfoIcon />
        </IconButton>
      </Box>
    </Tooltip>
  );
}
